import { useAuth, UserRole } from "@/features/auth";
import { useApiClient } from "@/infrastructure/rest";
import { DossierOverzichtEvent, useRealtimeTopic } from "@/infrastructure/rsocket";
import {
    DossierDetailsDto,
    OngewensteGebeurtenisDto,
    OngewensteGebeurtenis as SpoorwebOngewensteGebeurtenisDto,
    ToegepasteVSMEventTypeToegepasteVSM,
} from "@/shared/types";
import { useMutation, useQueries, useQuery, useQueryClient } from "@tanstack/react-query";
import { create } from "zustand";
import { persist } from "zustand/middleware";

/* Several hooks and utilities for creating OGs and running scenarios */

const OG_DETAILS_QUERY_KEY = "og-details";
const DOSSIERS_BY_SPOORWEB_ID_QUERY_KEY = "dossiers-by-spoorweb-id";

export function usePostOngewensteGebeurtenis() {
    const queryClient = useQueryClient();
    const apiClient = useApiClient();

    return useMutation({
        mutationFn: async (og: SpoorwebOngewensteGebeurtenisDto) => {
            await apiClient.post(`/debug/ongewenste-gebeurtenissen`, og);
        },
        onSuccess: async () => {
            await queryClient.invalidateQueries({ queryKey: [OG_DETAILS_QUERY_KEY] });
        },
        onError: (error) => {
            console.error("Error processing ongewenste gebeurtenis", error);
        },
    });
}

export function usePostTVSM() {
    const queryClient = useQueryClient();
    const apiClient = useApiClient();

    return useMutation({
        mutationFn: async (tvsm: ToegepasteVSMEventTypeToegepasteVSM) => {
            await apiClient.post(`/debug/toegepaste-versperringsmaatregel`, tvsm);
        },
        onSuccess: async () => {
            await queryClient.invalidateQueries({ queryKey: [OG_DETAILS_QUERY_KEY] });
        },
        onError: (error) => {
            console.error("Error processing ongewenste gebeurtenis", error);
        },
    });
}

export function useGetOngewensteGebeurtenissenDetails() {
    const apiClient = useApiClient();
    return useQuery({
        queryKey: [OG_DETAILS_QUERY_KEY],
        queryFn: async () => {
            const data = await apiClient.get<OngewensteGebeurtenisDto[]>("/regievoerder/ongewenste-gebeurtenissen");
            return (
                data?.map((dto) => ({
                    id: dto.ongewensteGebeurtenis.incident.melding.meldingId,
                    volgnummer: dto.ongewensteGebeurtenis.volgnummer,
                    prognoses: dto.ongewensteGebeurtenis.incident.prognoses?.prognose ?? [],
                })) ?? []
            );
        },
    });
}

type DossierOmschrijvingen = {
    forOgId: string;
    omschrijving?: string;
    omschrijvingExtraContext?: string | undefined;
};

export function useGetDossierDetailsBySpoorwebId(ids: string[]) {
    const apiClient = useApiClient();

    return useQueries({
        combine: (queries) => queries.map((query) => query.data),
        queries: ids.map((spoorwebId) => ({
            queryKey: [DOSSIERS_BY_SPOORWEB_ID_QUERY_KEY, spoorwebId],
            queryFn: async () => {
                const data = await apiClient.get<DossierDetailsDto>(`/debug/ongewenste-gebeurtenissen/${spoorwebId}/dossiers`);
                if (data) {
                    return {
                        forOgId: spoorwebId,
                        omschrijving: data.omschrijving,
                        omschrijvingExtraContext: data.omschrijvingExtraContext,
                    } satisfies DossierOmschrijvingen;
                }
                return {
                    forOgId: spoorwebId,
                };
            },
        })),
    });
}

export function useRefetchDossierDetailsOnCreate() {
    const queryClient = useQueryClient();
    const { hasRole } = useAuth();

    // Used only on the admin-only scenarios debug page. The role gate
    // prevents subscribing on the wrong apiDomain if wired up elsewhere.
    useRealtimeTopic<DossierOverzichtEvent>("dossierOverzicht", {
        enabled: hasRole(UserRole.REGIEVOERDER),
        onMessage: () => {
            setTimeout(() => {
                void queryClient.invalidateQueries({ queryKey: [DOSSIERS_BY_SPOORWEB_ID_QUERY_KEY], exact: false });
            }, 2000);
        },
    });
}

type ScenarioId = string;
type OngewensteGebeurtenisId = string;

type ScenarioStore = {
    addOngewensteGebeurtenis: (scenarioId: string, ogId: OngewensteGebeurtenisId) => void;
    reset: () => void;
    scenarios: Record<ScenarioId, OngewensteGebeurtenisId[]>;
};

export const useScenarioStore = create<ScenarioStore>()(
    persist(
        (set, get) => ({
            scenarios: {},
            reset: () => set({ scenarios: {} }),
            addOngewensteGebeurtenis: (scenarioId: string, ogId: OngewensteGebeurtenisId) => {
                const ogsForScenario = Object.entries(get().scenarios).flatMap(([sid, ids]) => (sid === scenarioId ? ids : []));

                set((state) => ({
                    scenarios: {
                        ...state.scenarios,
                        [scenarioId]: [...ogsForScenario, ogId],
                    },
                }));
            },
        }),
        {
            name: "scenarios-state",
        }
    )
);
